import { useMemo } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useWorkshops } from '../../hooks/useWorkshops';
import { Card, Table } from '../ui';
import { Accordion } from '../ui';

export default function ClientWorkshopsTab({ clientId }) {
  const { data: allBookings = [], isLoading } = useWorkshops();

  const bookings = useMemo(
    () => allBookings.filter((b) => b.companyId === clientId || b.existingCompanyId === clientId),
    [allBookings, clientId],
  );

  const approved = useMemo(() => bookings.filter((b) => b.status === 'approved'), [bookings]);
  const pending  = useMemo(() => bookings.filter((b) => b.status === 'pending'),  [bookings]);
  const denied   = useMemo(() => bookings.filter((b) => b.status === 'denied'),   [bookings]);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-text-muted">Loading workshops…</div>;
  }

  if (bookings.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-text-muted text-center py-4">No workshop bookings for this client.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Approved */}
      <div>
        <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">
          Approved Workshops ({approved.length})
        </h2>
        {approved.length === 0 ? (
          <Card padding="md">
            <p className="text-sm text-text-muted text-center py-2">No approved workshops.</p>
          </Card>
        ) : (
          <Card padding="none">
            <Table
              cellPadding="compact"
              headers={[
                { label: 'Date / Time' },
                { label: 'Location' },
                { label: 'Contact' },
                { label: 'Approved By' },
                { label: '' },
              ]}
              rows={approved.map((b) => ({
                cells: [
                  {
                    content: <span className="text-sm font-medium tabular-nums">{b.preferredDate} · {b.preferredTime}</span>,
                  },
                  {
                    content: <span className="text-xs text-text-muted">{b.location}</span>,
                  },
                  {
                    content: <span className="text-xs text-text-muted">{b.contactName} · {b.contactEmail}</span>,
                  },
                  {
                    content: <span className="text-xs text-text-muted">{b.approvedBy}</span>,
                  },
                  {
                    content: b.calendarEventLink ? (
                      <a
                        href={b.calendarEventLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-brand-primary hover:underline transition-colors flex-shrink-0"
                      >
                        <CalendarDaysIcon className="w-3.5 h-3.5" />
                        View in Calendar
                      </a>
                    ) : null,
                    className: 'text-right',
                  },
                ],
              }))}
            />
          </Card>
        )}
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div>
          <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">
            Pending <span className="text-amber-500">({pending.length})</span>
          </h2>
          <Card padding="none">
            <div className="divide-y divide-border">
              {pending.map((b) => (
                <div key={b.id} className="flex items-center gap-4 px-4 py-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{b.preferredDate} · {b.preferredTime}</p>
                    <p className="text-xs text-text-muted">{b.location}</p>
                  </div>
                  <span className="text-[11px] px-1.5 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Pending
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Denied */}
      {denied.length > 0 && (
        <Accordion title={`Denied (${denied.length})`} defaultOpen={false}>
          <div className="divide-y divide-border">
            {denied.map((b) => (
              <div key={b.id} className="flex items-center gap-4 py-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{b.preferredDate} · {b.preferredTime}</p>
                  <p className="text-xs text-text-muted">{b.location}</p>
                </div>
                {b.denialReason && (
                  <p className="text-xs text-text-muted italic">"{b.denialReason}"</p>
                )}
              </div>
            ))}
          </div>
        </Accordion>
      )}
    </div>
  );
}
